import React, { useState } from 'react';
import { Input } from '../../ui/input';
import { Search, Loader2 } from 'lucide-react';
import { useDatasetSearch } from './hooks/useDatasetSearch';

const LIBRARY_BADGES = {
  reference: 'bg-emerald-100 text-emerald-700',
  system: 'bg-slate-100 text-slate-600',
  user: 'bg-sky-100 text-sky-700',
};

export default function DatasetSearchDropdown({ datasets, value, onChange, placeholder, category }) {
  const {
    search,
    isOpen, setIsOpen,
    filteredDatasets,
    loading,
    inputRef, dropdownRef,
    handleSelect,
    handleInputChange
  } = useDatasetSearch(datasets, category, value);

  // Per-option chosen geography variant (keyed by option name+unit).
  const [chosenVariants, setChosenVariants] = useState({});

  const optionKey = (ds) => `${ds.name}|${ds.unit}`;

  const variantFor = (ds) => {
    const chosenId = chosenVariants[optionKey(ds)];
    return ds.variants?.find(v => v.id === chosenId)
      || ds.variants?.find(v => v.id === ds.id)
      || ds.variants?.[0];
  };

  const selectOption = (ds) => {
    const variant = variantFor(ds) || { id: ds.id, geography: ds.geography, gwp: ds.emission_factor };
    handleSelect({
      ...ds,
      id: variant.id,
      geography: variant.geography,
      emission_factor: variant.gwp,
    }, onChange);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
        <Input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => handleInputChange(e, onChange)}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="h-8 text-sm pl-7 bg-white"
          data-testid="dataset-search-input"
        />
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-[100] w-96 mt-1 bg-white border border-slate-200 rounded-md shadow-xl max-h-72 overflow-auto"
          style={{ left: 0, top: '100%' }}
        >
          {filteredDatasets.length > 0 ? (
            filteredDatasets.map((ds) => {
              const variant = variantFor(ds);
              const gwp = variant?.gwp;
              return (
                <button
                  key={optionKey(ds)}
                  onClick={() => selectOption(ds)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-sky-50 flex items-center justify-between gap-2 border-b border-slate-100 last:border-b-0"
                  data-testid={`dataset-option-${variant?.id || ds.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{ds.name}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${LIBRARY_BADGES[ds.source_type] || LIBRARY_BADGES.system}`}>
                        {ds.library}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-mono">
                        per {ds.unit}
                      </span>
                      {ds.variants?.length > 1 ? (
                        <select
                          value={variant?.id || ''}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation();
                            setChosenVariants({ ...chosenVariants, [optionKey(ds)]: e.target.value });
                          }}
                          className="text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white text-slate-600"
                        >
                          {ds.variants.map(v => (
                            <option key={v.id} value={v.id}>{v.geography || '—'}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-[10px] text-slate-500">{variant?.geography || ds.geography || ''}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs font-mono text-emerald-700 bg-emerald-50 px-2 py-1 rounded whitespace-nowrap">
                    GWP: {gwp != null ? Number(gwp).toLocaleString(undefined, { maximumSignificantDigits: 4 }) : '—'}
                  </div>
                </button>
              );
            })
          ) : loading ? (
            <div className="px-3 py-4 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Searching…
            </div>
          ) : (
            <div className="px-3 py-4 text-sm text-slate-500 text-center">
              No matching datasets found for "{search}"
            </div>
          )}
          {loading && filteredDatasets.length > 0 && (
            <div className="px-3 py-1.5 text-[11px] text-slate-400 flex items-center gap-1.5 border-t border-slate-100">
              <Loader2 className="h-3 w-3 animate-spin" /> Updating results…
            </div>
          )}
        </div>
      )}
    </div>
  );
}
